/**
 * Запуск генерации пакета и история версий (ТЗ FR-28…FR-34).
 *
 * Перед генерацией профиль сохраняется: документ должен собираться из того,
 * что записано в карточке, а не из состояния браузера.
 */

import { useState } from 'react';
import type { CaseProfile, Readiness } from '@bd/domain';
import type { DocumentKind } from '@bd/doc-engine';
import { api, ApiError } from '../../api.js';
import { VersionList } from './VersionList.js';
import { markGenerated } from '../../storage/cases.js';

const DOCUMENTS: Array<[kind: DocumentKind, title: string]> = [
  ['zayavlenie', 'Заявление'],
  ['opis-imushchestva', 'Опись имущества'],
  ['spisok-kreditorov', 'Список кредиторов'],
];

interface GeneratedFile {
  kind: DocumentKind;
  fileName: string;
  format: 'docx' | 'pdf';
  downloadUrl?: string;
}

export function GeneratePanel({
  caseId,
  profile,
  readiness,
  readOnly,
  folderId,
  beforeGenerate,
}: {
  caseId: string;
  profile: CaseProfile;
  readiness: Readiness;
  readOnly: boolean;
  folderId?: string;
  /** Сохранение профиля перед генерацией */
  beforeGenerate: () => Promise<CaseProfile | null>;
}) {
  const [selected, setSelected] = useState<DocumentKind[]>(DOCUMENTS.map(([kind]) => kind));
  const [withPdf, setWithPdf] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ version: number; files: GeneratedFile[]; warnings: string[]; durationMs: number } | null>(null);
  const [error, setError] = useState<{ message: string; details: string[]; requestId?: string } | null>(null);
  /** Счётчик формирований: по нему список версий перечитывается */
  const [generations, setGenerations] = useState(0);

  const generate = async () => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const saved = (await beforeGenerate()) ?? profile;
      const response = await api.generate({
        caseId,
        profile: saved,
        documents: selected,
        formats: withPdf ? ['docx', 'pdf'] : ['docx'],
        folderId,
      });
      setResult({ version: response.packageVersion, files: response.files, warnings: response.warnings, durationMs: response.durationMs });
      setGenerations((count) => count + 1);

      /*
        Отметка в карточке сделки: когда собран пакет и где лежит его папка.

        Нужна двоим. Менеджеру — чтобы видеть в списке дел, что документы
        готовы: иначе поле «Последняя генерация» остаётся пустым навсегда.
        И самому приложению — без сохранённого идентификатора папки сервер
        при каждой сборке перебирает корень Диска, разыскивая её по имени
        «Дело N». Пока дел десяток, это незаметно; на потоке — лишняя работа
        и риск завести вторую папку, если первую переименуют.

        Сбой здесь не отменяет собранного пакета: файлы уже на Диске,
        и пугать ими юриста незачем.
      */
      await markGenerated(caseId, response.folderId).catch(() => undefined);
    } catch (e) {
      const apiError = e as ApiError;
      setError({
        message: apiError.message,
        details: apiError.details ?? [],
        requestId: apiError.requestId,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="block generate">
      <h2>Формирование пакета</h2>

      <div className="checkbox-row">
        {DOCUMENTS.map(([kind, title]) => (
          <label key={kind} className="field field-inline">
            <input
              type="checkbox"
              checked={selected.includes(kind)}
              disabled={readOnly}
              onChange={(e) => setSelected((current) => (e.target.checked ? [...current, kind] : current.filter((k) => k !== kind)))}
            />
            <span>{title}</span>
          </label>
        ))}
      </div>

      <label className="field field-inline">
        <input type="checkbox" checked={withPdf} disabled={readOnly} onChange={(e) => setWithPdf(e.target.checked)} />
        <span>Дополнительно PDF</span>
      </label>

      <p className="hint">
        Файлы появятся в папке дела на Диске Битрикс24. Каждое формирование создаёт новую версию,
        прежние сохраняются. PDF собирается службой конвертации в контуре заказчика: наружу
        документ не уходит.
      </p>

      <button
        type="button"
        className="primary"
        disabled={readOnly || busy || selected.length === 0 || !readiness.canGenerate}
        onClick={generate}
      >
        {busy ? 'Формирование…' : 'Сформировать пакет'}
      </button>

      {!readiness.canGenerate && (
        <p className="error-text">
          Генерация недоступна: не пройдены обязательные проверки. Перечень ошибок — в панели готовности.
        </p>
      )}

      {error && (
        <div className="error-box">
          <strong>{error.message}</strong>
          {error.details.length > 0 && (
            <ul>
              {error.details.map((detail, i) => (
                <li key={i}>{detail}</li>
              ))}
            </ul>
          )}
          {error.requestId && <p className="hint">Идентификатор запроса для администратора: {error.requestId}</p>}
        </div>
      )}

      {result && (
        <div className="success-box">
          <strong>Пакет версии {result.version} сформирован за {(result.durationMs / 1000).toFixed(1)} с</strong>
          <ul>
            {result.files.map((file, i) => (
              <li key={i}>
                {file.downloadUrl ? (
                  <a href={file.downloadUrl} target="_blank" rel="noreferrer">
                    {file.fileName}
                  </a>
                ) : (
                  file.fileName
                )}
                {file.format === 'pdf' && ' (PDF)'}
              </li>
            ))}
          </ul>
          {result.warnings.length > 0 && (
            <>
              <p className="warning">Сформировано с предупреждениями:</p>
              <ul>
                {result.warnings.map((warning, i) => (
                  <li key={i}>{warning}</li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      <h3>История версий</h3>
      <VersionList caseId={caseId} folderId={folderId} reloadKey={generations} />

    </section>
  );
}
